/**
 * Serviço Unificado de Gerenciamento de Prazos
 * Centraliza toda a lógica de prazos processuais
 */

import { FirebaseService } from './firebaseService';
import cacheService from './cacheService';

class PrazoService {
  
  /**
   * Buscar todos os prazos do usuário (unificado)
   */
  static async getUserPrazos(userId) {
    try {
      const cacheKey = `prazos_${userId}`;
      
      // Usar cache inteligente
      return await cacheService.cached(cacheKey, async () => {
        console.log('📋 Buscando prazos unificados do usuário:', userId);
        
        // Buscar comunicações processadas com prazos
        const comunicacoes = await FirebaseService.getUserPjeComunicacoes(userId);
        const processos = await FirebaseService.getUserProcessos(userId);
      
      const prazos = [];
      
      // Extrair prazos das comunicações processadas
      comunicacoes.forEach(com => {
        if (com.aiAnalysis && com.aiAnalysis.temPrazo && com.aiAnalysis.prazos) {
          com.aiAnalysis.prazos.forEach(prazo => {
            // Validar e corrigir dados do prazo
            const prazoValidado = this.validarPrazo(prazo, com);

            if (prazoValidado) {
              prazos.push({
                id: `com_${com.id}_${prazo.tipo}`,
                fonte: 'comunicacao',
                comunicacaoId: com.id,
                processoId: com.processoId,
                numeroProcesso: com.numeroProcesso || com.aiAnalysis.numeroProcesso,
                cliente: com.aiAnalysis.cliente || `Processo ${com.siglaTribunal}`,
                tribunal: com.siglaTribunal,

                // Dados do prazo validados
                tipo: prazoValidado.tipo,
                tipoAto: com.aiAnalysis.tipoAto || 'comunicação',
                descricao: prazoValidado.descricao,
                dias: prazoValidado.dias,
                diasUteis: prazoValidado.diasUteis,
                dataInicio: prazoValidado.dataInicio,
                dataVencimento: prazoValidado.dataVencimento,
                dataPublicacao: com.data_disponibilizacao,
                diasRestantes: prazoValidado.diasRestantes,
                urgencia: prazoValidado.urgencia,
                status: prazo.status || 'pendente',
                observacoes: prazo.observacoes || '',
                
                // Dados de conclusão se estiver concluído
                dataConclusao: prazo.status === 'concluido' ? (com.concluidoEm || new Date().toISOString()) : null,

                // Dados da comunicação original
                textoCompleto: com.texto,

                // Metadados
                dataAnalise: com.aiAnalysis.dataAnalise,
                analisadoPorIA: com.aiAnalysis.analisadoPorIA,
                acoesSugeridas: com.aiAnalysis.acoesSugeridas || [],
                tags: com.aiAnalysis.tags || []
              });
            }
          });
        }
      });
      
      // Extrair prazos dos processos cadastrados manualmente
      processos.forEach(proc => {
        if (proc.proximoPrazo) {
          prazos.push({
            id: `proc_${proc.id}`,
            fonte: 'processo',
            processoId: proc.id,
            numeroProcesso: proc.numero,
            cliente: proc.cliente,
            tribunal: 'Manual',
            
            // Dados do prazo
            tipo: 'prazo_processo',
            descricao: `Próximo prazo - ${proc.tipo}`,
            dias: 0,
            diasUteis: false,
            dataInicio: proc.dataAdicao?.split('T')[0] || new Date().toISOString().split('T')[0],
            dataVencimento: proc.proximoPrazo,
            diasRestantes: this.calcularDiasRestantes(proc.proximoPrazo),
            urgencia: this.calcularUrgencia(proc.proximoPrazo),
            status: proc.concluido ? 'concluido' : 'pendente',
            
            // Dados de conclusão se estiver concluído
            dataConclusao: proc.concluido ? (proc.dataConclusao || new Date().toISOString()) : null,
            
            // Metadados
            dataAnalise: proc.dataAdicao,
            analisadoPorIA: false,
            acoesSugeridas: [],
            tags: ['manual']
          });
        }
      });
      
      // Ordenar por urgência e data de vencimento
      prazos.sort((a, b) => {
        // 🔥 CORREÇÃO: Incluir 'vencido' na ordenação
        const urgenciaOrder = { 'vencido': 0, 'hoje': 1, 'esta_semana': 2, 'proximo_mes': 3 };
        const urgenciaA = urgenciaOrder[a.urgencia] || 4;
        const urgenciaB = urgenciaOrder[b.urgencia] || 4;
        
        if (urgenciaA !== urgenciaB) return urgenciaA - urgenciaB;
        return new Date(a.dataVencimento) - new Date(b.dataVencimento);
      });
      
        console.log(`✅ ${prazos.length} prazos encontrados`);
        return prazos;
      });
      
    } catch (error) {
      console.error('❌ Erro ao buscar prazos do usuário:', error);
      throw error;
    }
  }
  
  /**
   * Validar e corrigir dados de prazo
   */
  static validarPrazo(prazo, comunicacao) {
    try {
      // Validar data de vencimento
      let dataVencimento = prazo.dataVencimento;
      let dataInicio = prazo.dataInicio || comunicacao.data_disponibilizacao;
      let dias = prazo.dias || 0;

      // Se data de vencimento é inválida, tentar recalcular
      if (!dataVencimento || dataVencimento === 'Invalid Date' || isNaN(new Date(dataVencimento))) {
        if (dataInicio && dias > 0) {
          const inicio = new Date(dataInicio);
          if (!isNaN(inicio)) {
            const vencimento = new Date(inicio);
            vencimento.setDate(vencimento.getDate() + dias);
            dataVencimento = vencimento.toISOString().split('T')[0];
          }
        } else {
          // Se não conseguir calcular, pular este prazo
          console.warn('❌ Prazo com data inválida ignorado:', prazo);
          return null;
        }
      }

      // Validar data de início
      if (!dataInicio || isNaN(new Date(dataInicio))) {
        dataInicio = comunicacao.data_disponibilizacao || new Date().toISOString().split('T')[0];
      }

      // Calcular dias se não informado
      if (!dias || dias <= 0) {
        if (dataInicio && dataVencimento) {
          const inicio = new Date(dataInicio);
          const vencimento = new Date(dataVencimento);
          if (!isNaN(inicio) && !isNaN(vencimento)) {
            dias = Math.ceil((vencimento - inicio) / (1000 * 60 * 60 * 24));
          }
        }
      }

      // Calcular dias restantes e urgência
      const diasRestantes = this.calcularDiasRestantes(dataVencimento);
      const urgencia = this.calcularUrgencia(dataVencimento);

      return {
        tipo: prazo.tipo || 'manifestação',
        descricao: prazo.descricao || 'Prazo processual',
        dias: Math.max(dias, 0),
        diasUteis: Boolean(prazo.diasUteis),
        dataInicio: dataInicio,
        dataVencimento: dataVencimento,
        diasRestantes: diasRestantes,
        urgencia: urgencia
      };

    } catch (error) {
      console.error('❌ Erro ao validar prazo:', error, prazo);
      return null;
    }
  }

  /**
   * Calcular dias restantes até o vencimento
   * IMPORTANTE: Não considera feriados por segurança jurídica
   * PERMITE valores negativos para prazos vencidos
   */
  static calcularDiasRestantes(dataVencimento) {
    if (!dataVencimento || isNaN(new Date(dataVencimento))) {
      return 0;
    }
    const hoje = new Date();
    const vencimento = new Date(dataVencimento);
    
    // 🔥 CORREÇÃO CRÍTICA: PERMITIR valores negativos para prazos vencidos
    const diasRestantes = Math.ceil((vencimento - hoje) / (1000 * 60 * 60 * 24));
    return diasRestantes; // Removido Math.max(0, diasRestantes)
  }

  /**
   * Calcular data de vencimento SEM considerar feriados
   * Por segurança jurídica, sempre calcula prazo mais apertado
   */
  static calcularDataVencimentoSegura(dataInicio, dias) {
    if (!dataInicio || !dias || dias <= 0) {
      return null;
    }

    const inicio = new Date(dataInicio);
    if (isNaN(inicio)) {
      return null;
    }

    // Adicionar dias corridos (sem considerar feriados)
    const vencimento = new Date(inicio);
    vencimento.setDate(vencimento.getDate() + dias);

    return vencimento.toISOString().split('T')[0];
  }

  /**
   * Verificar se é fim de semana (apenas informativo)
   */
  static isFimDeSemana(data) {
    const dia = new Date(data).getDay();
    return dia === 0 || dia === 6; // Domingo ou Sábado
  }

  /**
   * Obter informações sobre o prazo (incluindo alertas)
   */
  static getInfoPrazo(dataVencimento) {
    if (!dataVencimento || isNaN(new Date(dataVencimento))) {
      return {
        diasRestantes: 0,
        urgencia: 'vencido',
        alertas: ['Data de vencimento inválida']
      };
    }

    const diasRestantes = this.calcularDiasRestantes(dataVencimento);
    const urgencia = this.calcularUrgencia(dataVencimento);
    const alertas = [];

    // Alertas informativos
    if (this.isFimDeSemana(dataVencimento)) {
      alertas.push('⚠️ Vence em fim de semana - Verifique se há prorrogação');
    }

    if (diasRestantes <= 3) {
      alertas.push('🚨 Prazo crítico - Verifique feriados e pontos facultativos');
    }

    if (diasRestantes <= 7) {
      alertas.push('⏰ Prazo próximo - Confirme data exata considerando feriados');
    }

    return {
      diasRestantes,
      urgencia,
      alertas,
      observacao: 'Cálculo não considera feriados por segurança jurídica'
    };
  }
  
  /**
   * Calcular urgência baseada na data de vencimento
   */
  static calcularUrgencia(dataVencimento) {
    if (!dataVencimento || isNaN(new Date(dataVencimento))) {
      return 'proximo_mes';
    }

    const diasRestantes = this.calcularDiasRestantes(dataVencimento);

    // 🔥 CORREÇÃO: Prazos vencidos devem ter urgência 'vencido'
    if (diasRestantes < 0) return 'vencido';
    if (diasRestantes === 0) return 'hoje';
    if (diasRestantes <= 7) return 'esta_semana';
    return 'proximo_mes';
  }
  
  /**
   * Marcar prazo como concluído
   */
  static async marcarPrazoConcluido(userId, prazo) {
    try {
      console.log('✅ Marcando prazo como concluído:', prazo.id);
      
      if (prazo.fonte === 'comunicacao') {
        // 🔥 CORREÇÃO: Buscar comunicação original do Firebase
        const comunicacoes = await FirebaseService.getUserPjeComunicacoes(userId);
        const comunicacao = comunicacoes.find(c => c.id === prazo.comunicacaoId);
        
        if (comunicacao && comunicacao.aiAnalysis && comunicacao.aiAnalysis.prazos) {
          // Atualizar prazo específico na comunicação
          const prazosAtualizados = comunicacao.aiAnalysis.prazos.map(p => 
            p.tipo === prazo.tipo ? { ...p, status: 'concluido' } : p
          );
          
          // Atualizar comunicação
          await FirebaseService.updateComunicacao(userId, prazo.comunicacaoId, {
            'aiAnalysis.prazos': prazosAtualizados,
            isRead: true,
            concluidoEm: new Date().toISOString()
          });
        }
        
        // Se vinculado a processo, marcar processo como concluído também
        if (prazo.processoId) {
          await FirebaseService.updateUserProcesso(userId, prazo.processoId, {
            concluido: true,
            dataConclusao: new Date().toISOString(),
            status: 'Concluído'
          });
        }
      } else if (prazo.fonte === 'processo') {
        // Marcar processo como concluído
        await FirebaseService.updateUserProcesso(userId, prazo.processoId, {
          concluido: true,
          dataConclusao: new Date().toISOString(),
          status: 'Concluído'
        });
      }
      
      console.log('✅ Prazo marcado como concluído');
      
    } catch (error) {
      console.error('❌ Erro ao marcar prazo como concluído:', error);
      throw error;
    }
  }
  
  /**
   * Reverter conclusão de prazo
   */
  static async reverterConclusaoPrazo(userId, prazo) {
    try {
      console.log('🔄 Revertendo conclusão do prazo:', prazo.id);
      
      if (prazo.fonte === 'comunicacao') {
        // 🔥 CORREÇÃO: Buscar comunicação original do Firebase
        const comunicacoes = await FirebaseService.getUserPjeComunicacoes(userId);
        const comunicacao = comunicacoes.find(c => c.id === prazo.comunicacaoId);
        
        if (comunicacao && comunicacao.aiAnalysis && comunicacao.aiAnalysis.prazos) {
          // Atualizar prazo específico na comunicação
          const prazosAtualizados = comunicacao.aiAnalysis.prazos.map(p => 
            p.tipo === prazo.tipo ? { ...p, status: 'pendente' } : p
          );
          
          // Atualizar comunicação
          await FirebaseService.updateComunicacao(userId, prazo.comunicacaoId, {
            'aiAnalysis.prazos': prazosAtualizados,
            isRead: false,
            concluidoEm: null
          });
        }
        
        // Se vinculado a processo, reabrir processo também
        if (prazo.processoId) {
          await FirebaseService.updateUserProcesso(userId, prazo.processoId, {
            concluido: false,
            dataConclusao: null,
            status: 'Em andamento'
          });
        }
      } else if (prazo.fonte === 'processo') {
        // Reabrir processo
        await FirebaseService.updateUserProcesso(userId, prazo.processoId, {
          concluido: false,
          dataConclusao: null,
          status: 'Em andamento'
        });
      }
      
      console.log('✅ Conclusão do prazo revertida');
      
    } catch (error) {
      console.error('❌ Erro ao reverter conclusão do prazo:', error);
      throw error;
    }
  }
  
  /**
   * Obter métricas de prazos
   */
  static async getMetricasPrazos(userId) {
    try {
      const cacheKey = `metricas_${userId}`;
      
      // Usar cache inteligente
      return await cacheService.cached(cacheKey, async () => {
        const prazos = await this.getUserPrazos(userId);
      
      const hoje = prazos.filter(p => p.urgencia === 'hoje' && p.status === 'pendente').length;
      const estaSemana = prazos.filter(p => p.urgencia === 'esta_semana' && p.status === 'pendente').length;
      const proximoMes = prazos.filter(p => p.urgencia === 'proximo_mes' && p.status === 'pendente').length;
      const concluidos = prazos.filter(p => p.status === 'concluido').length;
      const total = prazos.length;
      
      const taxaCumprimento = total > 0 ? Math.round((concluidos / total) * 100) : 0;
      
        return {
          hoje,
          estaSemana,
          proximoMes,
          concluidos,
          total,
          taxaCumprimento,
          vencidos: prazos.filter(p => (p.urgencia === 'vencido' || p.diasRestantes < 0) && p.status === 'pendente').length
        };
      });
      
    } catch (error) {
      console.error('❌ Erro ao calcular métricas de prazos:', error);
      return {
        hoje: 0,
        estaSemana: 0,
        proximoMes: 0,
        concluidos: 0,
        total: 0,
        taxaCumprimento: 0,
        vencidos: 0
      };
    }
  }
  
  /**
   * Editar prazo
   */
  static async editarPrazo(userId, prazoAtualizado) {
    try {
      console.log('✏️ Editando prazo:', prazoAtualizado.id);

      if (prazoAtualizado.fonte === 'comunicacao') {
        // Buscar comunicação original
        const comunicacoes = await FirebaseService.getUserPjeComunicacoes(userId);
        const comunicacao = comunicacoes.find(c => c.id === prazoAtualizado.comunicacaoId);

        if (comunicacao && comunicacao.aiAnalysis && comunicacao.aiAnalysis.prazos) {
          // Atualizar prazo específico na comunicação
          const prazosAtualizados = comunicacao.aiAnalysis.prazos.map(p => {
            if (p.tipo === prazoAtualizado.tipo) {
              return {
                ...p,
                tipo: prazoAtualizado.tipo,
                descricao: prazoAtualizado.descricao,
                dias: prazoAtualizado.dias,
                dataVencimento: prazoAtualizado.dataVencimento,
                diasRestantes: this.calcularDiasRestantes(prazoAtualizado.dataVencimento),
                urgencia: this.calcularUrgencia(prazoAtualizado.dataVencimento),
                observacoes: prazoAtualizado.observacoes,
                dataEdicao: prazoAtualizado.dataEdicao
              };
            }
            return p;
          });

          // Salvar comunicação atualizada
          await FirebaseService.updateComunicacao(userId, prazoAtualizado.comunicacaoId, {
            'aiAnalysis.prazos': prazosAtualizados,
            dataUltimaEdicao: new Date().toISOString()
          });
        }
      } else if (prazoAtualizado.fonte === 'processo') {
        // Atualizar processo
        await FirebaseService.updateUserProcesso(userId, prazoAtualizado.processoId, {
          proximoPrazo: prazoAtualizado.dataVencimento,
          tipo: prazoAtualizado.tipo,
          observacoes: prazoAtualizado.observacoes,
          dataUltimaEdicao: new Date().toISOString()
        });
      }

      console.log('✅ Prazo editado com sucesso');

    } catch (error) {
      console.error('❌ Erro ao editar prazo:', error);
      throw error;
    }
  }

  /**
   * Filtrar prazos por critério
   */
  static filtrarPrazos(prazos, filtro) {
    switch (filtro) {
      case 'hoje':
        return prazos.filter(p => p.urgencia === 'hoje' && p.status === 'pendente');
      case 'esta_semana':
        return prazos.filter(p => p.urgencia === 'esta_semana' && p.status === 'pendente');
      case 'vencidos':
        return prazos.filter(p => (p.urgencia === 'vencido' || p.diasRestantes < 0) && p.status === 'pendente');
      case 'concluidos':
        return prazos.filter(p => p.status === 'concluido');
      case 'pendentes':
        return prazos.filter(p => p.status === 'pendente');
      default:
        return prazos;
    }
  }
}

export { PrazoService };
export default PrazoService;
